import { CommonModule } from '@angular/common';
import { Component, computed, inject, Injector, OnDestroy, OnInit } from '@angular/core';
import {
  RecordTimestampDialogComponent,
  SOFTLINE_FEATURE_TIMESTAMP,
  SOFTLINE_FEATURE_TIMESTAMP_CURRENT_EMPLOYEE,
  TimestampStore,
} from '@softapps/pms/stempelerfassung';
import { showRequestErrors } from '@softline/application';
import {
  AuthenticationContextStore,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
} from '@softline/auth';
import { DateService, Store, Trace } from '@softline/core';
import {
  MessageBarStore,
  modalFn,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  translator,
} from '@softline/ui-core';
import moment from 'moment/moment';
import { Subscription } from 'rxjs';
import { TimestampCurrentEmployeeStore } from '../../store/current-employee.store';
import { StampCaptureService } from '../work-hours/work-hours.service';

import { IconComponent, RepeatDirective, TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-stempelerfassung-widget',
  imports: [CommonModule, IconComponent, RepeatDirective, TranslatePipe],
  templateUrl: './stempelerfassung.widget.html',
  styleUrls: ['./stempelerfassung.widget.css'],
})
export class StempelerfassungWidget implements OnInit, OnDestroy {
  private store = inject(Store);
  private dateService = inject(DateService);
  private injector = inject(Injector);
  private readonly t = translator();
  modal = modalFn();
  stampCaptureService = inject(StampCaptureService);

  private subscription?: Subscription;

  currentEmployee$ = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP_CURRENT_EMPLOYEE,
    TimestampCurrentEmployeeStore.getters.data
  );
  loaded = this.store.signal(SOFTLINE_FEATURE_TIMESTAMP, TimestampStore.getters.loaded);
  loading = this.store.signal(SOFTLINE_FEATURE_TIMESTAMP, TimestampStore.getters.loading);

  private allTimestamps = this.store.signal(
    SOFTLINE_FEATURE_TIMESTAMP,
    TimestampStore.getters.all
  );

  timestamps = computed(() => {
    const timestamps = this.allTimestamps();
    const date = this.dateService.now();
    return timestamps
      .filter((o) => moment(o.timestamp).startOf('day').isSame(date))
      .sort((a, b) =>
        a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
      );
  });

  async ngOnInit(): Promise<void> {
    const context = this.store.get(
      SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
      AuthenticationContextStore.getters.data
    );
    const userId = (context as any).userId;
    await this.store.dispatch(
      SOFTLINE_FEATURE_TIMESTAMP_CURRENT_EMPLOYEE,
      TimestampCurrentEmployeeStore.actions.loadEmployee,
      { userId }
    );
    this.subscription = this.currentEmployee$.subscribe(async (employee) => {
      if (!employee) return;
      const date = this.dateService.now();
      this.store.commit(SOFTLINE_FEATURE_TIMESTAMP, TimestampStore.mutations.clear);
      await this.store.dispatch(
        SOFTLINE_FEATURE_TIMESTAMP,
        TimestampStore.actions.loadMany,
        {
          queryParams: {
            from: date.substr(0, 10),
            to: date.substr(0, 10),
            employeeid: employee.id,
          },
        }
      );
    });
  }

  ngOnDestroy() {
    if (this.subscription && !this.subscription.closed) this.subscription.unsubscribe();
    this.subscription = undefined;
  }

  @Trace()
  async onCreate(): Promise<void> {
    const result = await this.modal({
      component: RecordTimestampDialogComponent,
      title: 'Stempel erfassen',
      subtitle: 'Stempelart und Uhrzeit auswählen',
      icon: 'fa-regular fa-clock',
    });

    if (result === 'DISMISSED') return;
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TIMESTAMP,
        TimestampStore.actions.create,
        { entity: result }
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title: '#TIMESTAMP.MESSAGES.ADD_SUCCESS.TITLE',
          message: '#TIMESTAMP.MESSAGES.ADD_SUCCESS.MESSAGE',
        }
      );
      if (result.flag === 'arrival') {
        this.stampCaptureService.isStamped.set(true);
      } else {
        this.stampCaptureService.isStamped.set(false);
      }
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
}
